import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface EoltReportData {
  reportId: string;
  fileName: string;
  url: string;
  vin?: string;
  createdOn?: string;
  createdByName?: string;
}

interface EoltReportViewDialogProps {
  open: boolean;
  onClose: () => void;
  reportData: EoltReportData | null;
}

const formatTimestamp = (ts: string | number | null | undefined): string => {
  if (!ts) return 'N/A';
  const num = Number(ts);
  if (!isNaN(num) && num > 0) {
    const date = new Date(num * 1000);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  }
  return String(ts);
};

const EoltReportViewDialog: React.FC<EoltReportViewDialogProps> = ({
  open,
  onClose,
  reportData,
}) => {
  if (!reportData) return null;

  const now = new Date();
  const timestamp = reportData.createdOn
    ? formatTimestamp(reportData.createdOn)
    : `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  const handleDownload = () => {
    if (reportData.fileName) {
      window.open(reportData.fileName, '_blank');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          fontSize: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        EOLT Report
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Log ID
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Timestamp
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Username
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Download report
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {reportData.reportId || '1'}
                </TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {timestamp}
                </TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                  {reportData.createdByName || 'N/A'}
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={!reportData.fileName}
                    sx={{
                      backgroundColor: '#58B80F1A',
                      color: '#58B80F',
                      borderRadius: '20px',
                      height: '28px',
                      padding: '0 16px',
                      fontSize: '12px',
                      fontWeight: 'normal',
                      textTransform: 'none',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: '#58B80F33',
                      },
                      border: '1px solid #58B80F',
                    }}
                  >
                    DOWNLOAD
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default EoltReportViewDialog;
